import { Z_INDEX } from "@/style/zIndex";
import { css } from "@emotion/react";
import { forwardRef, useEffect, useMemo, useRef } from "react";
import type { CalendarProps } from "react-calendar";

import { Calendar } from "@/component/common/dateTimePicker/Calendar";
import { TimePicker } from "@/component/common/dateTimePicker/TimePicker";
import { useDateTimePicker } from "@/hooks/useDateTimePicker";
import { getTimeStringFromDate } from "@/utils/formatDate";
import { isPast } from "date-fns";
import { useToast } from "@/hooks/useToast";
import { getDeviceType } from "@/utils/deviceUtils";

type DesktopDateTimePickerProps = {
  /**
   * Pass a function to determine if a certain day should be displayed as disabled.
   *
   * @example ({ activeStartDate, date, view }) => date.getDay() === 0
   */
  tileDisabled?: CalendarProps["tileDisabled"];
  defaultValue?: string | Date;
  onSave: (dateTime?: string) => void;
};

const DesktopDateTimePicker = forwardRef<HTMLDivElement, DesktopDateTimePickerProps>(function DesktopDateTimePicker(
  { defaultValue, tileDisabled, onSave },
  ref,
) {
  const { toast } = useToast();
  const defaultDate = useMemo(() => (typeof defaultValue === "string" ? new Date(defaultValue) : defaultValue), [defaultValue]);
  const { onSelectDate, radioControl, date, dateTime, time } = useDateTimePicker(defaultDate, getTimeStringFromDate(defaultDate));
  const timePickerRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = getDeviceType();

  useEffect(() => {
    if (date) {
      timePickerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [date]);

  /* 데스크탑에서 시간 변경 상태를 감지 후 저장 로직 */
  useEffect(() => {
    if (isDesktop && dateTime && time) {
      handleClickSave();
    }
  }, [dateTime, isDesktop, time]);

  const handleClickSave = () => {
    if (dateTime && isPast(dateTime)) {
      toast.error("과거는 선택할 수 없어요");
      return;
    }

    onSave(dateTime);
  };

  return (
    <div
      css={css`
        position: absolute;
        top: -15rem;
        right: 6rem;
        width: 26rem;
        min-height: 33.8rem;
        display: flex;
        flex-direction: column;
        padding: 1.6rem 1.2rem;
        border-radius: 1.2rem;
        background-color: #fff;
        box-shadow:
          0 4px 12px 0 rgba(6, 8, 12, 0.12),
          0 4px 8px 0 rgba(6, 8, 12, 0.08);
        z-index: ${Z_INDEX.localOverlay};
        gap: ${isDesktop ? "1.6rem" : "2.8rem"};
      `}
      ref={ref}
    >
      <div
        css={css`
          min-height: 23.3rem;
        `}
      >
        <Calendar defaultValue={defaultValue} onChange={onSelectDate} tileDisabled={tileDisabled} />
      </div>
      {date && <TimePicker ref={timePickerRef} radioControl={radioControl} />}
    </div>
  );
});

DesktopDateTimePicker.displayName = "DesktopDateTimePicker";
export { DesktopDateTimePicker };
